import { useLocation } from 'wouter';
import { useUIStore } from '../../hooks/useUIStore';
import { useAuthStore } from '../../hooks/useAuth';
import {
    CheckCircle2,
    Circle,
    Building2,
    DoorOpen,
    Package,
    Layers,
    CalendarCheck,
    ShieldCheck,
    Truck,
    ChevronRight,
    Sparkles,
    X,
    Users,
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

export interface OnboardingSignals {
    hasWarehouse: boolean;
    hasDocks: boolean;
    hasCustomers: boolean;
    hasPOs: boolean;
    hasLoads: boolean;
    hasAppointments: boolean;
    hasApproved: boolean;
    hasCheckedIn: boolean;
}

interface ChecklistItem {
    id: string;
    label: string;
    description: string;
    complete: boolean;
    icon: React.ElementType;
    ctaLabel: string;
    group: 'setup' | 'shipment' | 'operation';
    action: () => void;
}

const GROUP_LABELS: Record<string, string> = {
    setup: 'Setup',
    shipment: 'First Shipment',
    operation: 'First Operation',
};

interface Props {
    signals: OnboardingSignals;
    forceExpanded?: boolean;
}

export function OnboardingChecklist({ signals, forceExpanded: forceExpandedProp }: Props) {
    const [, setLocation] = useLocation();
    const { togglePOWizard } = useUIStore();
    const { user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';
    const [dismissed, setDismissed] = useState(false);

    const items: ChecklistItem[] = [
        {
            id: 'warehouse',
            label: 'Set up your warehouse',
            description: 'Create your facility profile with address and operating hours.',
            complete: signals.hasWarehouse,
            icon: Building2,
            ctaLabel: 'Complete Profile',
            group: 'setup',
            action: () => setLocation('/facilities?from=onboarding'),
        },
        {
            id: 'docks',
            label: 'Add your dock doors',
            description: 'Configure at least one receiving dock for inbound trucks.',
            complete: signals.hasDocks,
            icon: DoorOpen,
            ctaLabel: 'Add Docks',
            group: 'setup',
            action: () => setLocation('/docks?from=onboarding'),
        },
        {
            id: 'customer',
            label: 'Add your first customer',
            description: 'Create a customer profile so POs can be linked to the right contacts.',
            complete: signals.hasCustomers,
            icon: Users,
            ctaLabel: 'Add Customer',
            group: 'setup',
            action: () => setLocation('/customers?from=onboarding'),
        },
        {
            id: 'po',
            label: 'Add your first PO',
            description: 'Create a purchase order to track an inbound shipment.',
            complete: signals.hasPOs,
            icon: Package,
            ctaLabel: 'Create PO',
            group: 'shipment',
            action: () => togglePOWizard(true),
        },
        ...(isOperationsEntitled ? [{
            id: 'load',
            label: 'Create your first load',
            description: 'Group POs into a load for a single truck movement.',
            complete: signals.hasLoads,
            icon: Layers,
            ctaLabel: 'Create Load',
            group: 'shipment' as const,
            action: () => setLocation('/loads?from=onboarding'),
        }] : []),
        {
            id: 'appointment',
            label: 'Schedule your first appointment',
            description: 'Book a dock time slot for an inbound delivery.',
            complete: signals.hasAppointments,
            icon: CalendarCheck,
            ctaLabel: 'Schedule',
            group: 'shipment',
            action: () => setLocation('/appointments/new?from=onboarding'),
        },
        {
            id: 'approve',
            label: 'Approve your first request',
            description: signals.hasAppointments
                ? 'Review and approve a carrier appointment request.'
                : 'A carrier needs to submit a booking request first. Share your Warehouse ID so they can book.',
            complete: signals.hasApproved,
            icon: ShieldCheck,
            ctaLabel: signals.hasAppointments ? 'Review requests' : 'Waiting for carrier request',
            group: 'operation',
            action: () => {
                const el = document.getElementById('dashboard-approval-queue');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    el.classList.add('ring-2', 'ring-primary', 'ring-offset-2', 'rounded-xl');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2', 'rounded-xl'), 3000);
                    toast('Review pending requests below and tap Approve, Revise, or Decline.', {
                        icon: '👇',
                        duration: 5000,
                    });
                } else {
                    toast('No pending requests yet — share your Warehouse ID with a carrier so they can submit a booking.', {
                        icon: 'ℹ️',
                        duration: 5000,
                    });
                }
            },
        },
        {
            id: 'checkin',
            label: 'Check in your first truck',
            description: signals.hasApproved
                ? 'Find an approved appointment on the dock board and tap "Check In".'
                : 'Approve an appointment first — then you can check in the truck when it arrives.',
            complete: signals.hasCheckedIn,
            icon: Truck,
            ctaLabel: signals.hasApproved ? 'Go to dock board' : 'Approve a request first',
            group: 'operation',
            action: () => {
                const el = document.getElementById('dashboard-dock-board');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    el.classList.add('ring-2', 'ring-primary', 'ring-offset-2', 'rounded-xl');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2', 'rounded-xl'), 3000);
                    toast('Find an approved appointment on the dock board and tap "Check In" to start.', {
                        icon: '👇',
                        duration: 5000,
                    });
                } else if (!signals.hasApproved) {
                    toast('Approve an appointment request first — that will place a truck on the dock board for check-in.', {
                        icon: 'ℹ️',
                        duration: 5000,
                    });
                } else {
                    toast('No dock board visible yet. An approved appointment will appear on the board shortly.', {
                        icon: 'ℹ️',
                        duration: 4000,
                    });
                }
            },
        },
    ];

    const completedCount = items.filter(i => i.complete).length;
    const totalCount = items.length;
    const allComplete = completedCount === totalCount;
    const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    // Find first incomplete item for highlighting
    const nextItem = items.find(i => !i.complete) ?? null;
    const nextItemId = nextItem?.id ?? null;

    // All complete — show compact success or hide entirely
    if (allComplete) {
        if (dismissed) return null;
        return (
            <div className="relative flex items-center gap-3 px-4 py-3 rounded-xl border bg-emerald-50 dark:bg-emerald-500/5 border-emerald-200 dark:border-emerald-500/20 animate-fade-in">
                <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex-1">
                    <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        Setup complete
                    </span>
                    <span className="text-xs text-emerald-600/70 dark:text-emerald-400/60 ml-2">
                        You're ready to manage inbound appointments.
                    </span>
                </div>
                <button
                    onClick={() => setDismissed(true)}
                    className="p-1 text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        );
    }

    // Group items
    const groups = ['setup', 'shipment', 'operation'] as const;

    // Part 5: Auto-expand when progress < 50%, auto-collapse when >= 80%
    // forceExpanded overrides auto-collapse (e.g. when returning from onboarding step)
    const autoExpanded = progress < 50;
    const autoCollapsed = progress >= 80;
    const [manualToggle, setManualToggle] = useState<boolean | null>(null);
    const isExpanded = forceExpandedProp
        ? (manualToggle !== null ? manualToggle : true)
        : (manualToggle !== null ? manualToggle : autoExpanded && !autoCollapsed);

    return (
        <div className="rounded-xl border bg-card border-border shadow-sm overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="px-5 pt-5 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-primary/10 rounded-lg">
                            <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                        <h3 className="text-sm font-bold text-foreground tracking-tight">
                            Get started with BayWhiz
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-muted-foreground tabular-nums">
                            {completedCount} / {totalCount}
                        </span>
                        <button
                            onClick={() => setManualToggle(prev => prev === null ? !isExpanded : !prev)}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={isExpanded ? 'Collapse checklist' : 'Expand checklist'}
                        >
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{
                            width: `${progress}%`,
                            background: progress >= 70
                                ? 'linear-gradient(90deg, #22c55e, #10b981)'
                                : 'linear-gradient(90deg, var(--color-primary), #6366f1)',
                        }}
                    />
                </div>
            </div>

            {/* Collapsible body */}
            {isExpanded && (
                <>
            {/* Next step header */}
            {nextItem && (
                <div className="px-5 pb-1">
                    <p className="text-sm font-medium text-muted-foreground">
                        Next step: <span className="text-foreground">{nextItem.label}</span>
                    </p>
                </div>
            )}

            {/* Checklist items grouped */}
            <div className="px-5 pb-5 space-y-4">
                {groups.map(group => {
                    const groupItems = items.filter(i => i.group === group);
                    if (groupItems.length === 0) return null;

                    return (
                        <div key={group} className="space-y-1">
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-0.5 mb-1.5">
                                {GROUP_LABELS[group]}
                            </div>
                            {groupItems.map(item => {
                                const isNext = item.id === nextItemId;

                                return (
                                    <div
                                        key={item.id}
                                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                                            item.complete
                                                ? 'opacity-60'
                                                : isNext
                                                ? 'bg-primary/5 dark:bg-primary/10 border border-primary/15 dark:border-primary/20'
                                                : 'hover:bg-muted/50'
                                        }`}
                                    >
                                        {/* Status icon */}
                                        <div className="shrink-0">
                                            {item.complete ? (
                                                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                            ) : (
                                                <Circle className={`w-5 h-5 ${isNext ? 'text-primary' : 'text-muted-foreground/40'}`} />
                                            )}
                                        </div>

                                        {/* Label + description */}
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-medium leading-tight ${
                                                item.complete
                                                    ? 'text-muted-foreground line-through decoration-muted-foreground/30'
                                                    : isNext ? 'text-foreground' : 'text-foreground/80'
                                            }`}>
                                                {item.label}
                                            </div>
                                            {isNext && (
                                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                                    {item.description}
                                                </p>
                                            )}
                                        </div>

                                        {/* CTA for next / incomplete items */}
                                        {!item.complete && (
                                            <button
                                                onClick={item.action}
                                                className={`shrink-0 flex items-center gap-1 text-xs font-semibold rounded-md px-3 py-1.5 transition-colors ${
                                                    isNext
                                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm animate-subtle-pulse'
                                                        : 'text-primary hover:bg-primary/10'
                                                }`}
                                            >
                                                {item.ctaLabel}
                                                <ChevronRight className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
                </>
            )}
        </div>
    );
}
